import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { AppShell } from "../components/AppShell";
import { CompanyLogo } from "../components/CompanyLogo";
import { Card, ErrorBox, Skeleton } from "../components/ui";
import { useContractorOverview } from "../lib/api";
import { fmtNumber } from "../lib/format";
import { GATE_PILL, STATUS_PILL } from "./contractors/shared";


/** Contractor management (CON-01 … CON-08): everyone working for the customer who is not staff — the firms that hold the
 * contracts and the people they put on site — in one place. Separate from People on purpose: a firm cannot be an
 * Employee, and contractors are asked different questions (is the engagement in date, may they be on site, what
 * has it cost) than staff are. */
export default function Contractors() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  // Procurement runs the firms on site day to day, so they edit the register alongside HR
  const canEdit = hasRole("os_hr") || hasRole("os_admin") || hasRole("os_procurement");
  const lng = i18n.language;
  const [company, setCompany] = useState("");
  const [search, setSearch] = useState("");
  const ov = useContractorOverview(company || undefined);
  const d = ov.data;
  const money = (v: number | null | undefined) => (v == null ? "–" : `${fmtNumber(v / 1e6, lng, 1)} M CDF`);

  const companies = useMemo(
    () => [...new Set((d?.contractors ?? []).map((c) => c.company).filter(Boolean))].sort() as string[],
    [d],
  );
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (d?.contractors ?? []).filter(
      (c) => !q || c.contractor_name.toLowerCase().includes(q) || (c.trade ?? "").toLowerCase().includes(q),
    );
  }, [d, search]);

  return (
    <AppShell subtitle={t("con.title")}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-brand-primary">{t("con.title")}</h1>
          <p className="mt-1 max-w-3xl text-sm text-brand-grey">{t("con.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {companies.length > 1 && (
            <label className="text-xs text-brand-grey">{t("con.company")}
              <select value={company} onChange={(e) => setCompany(e.target.value)} data-con-company
                className="ml-2 rounded border border-brand-grey-light bg-white px-2 py-1 text-sm text-brand-charcoal">
                <option value="">{t("common.all")}</option>
                {companies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          )}
          {canEdit && (
            <Link to="/contractors/new" data-con-add
              className="rounded-md bg-brand-primary px-3 py-2 text-xs font-semibold text-white hover:bg-brand-deep">
              + {t("con.form.addTitle")}
            </Link>
          )}
        </div>
      </div>

      {ov.isError && <div className="mt-6"><ErrorBox error={ov.error} onRetry={() => void ov.refetch()} /></div>}
      {ov.isLoading && <div className="mt-6 grid gap-4 md:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-28" />)}</div>}

      {d && (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-con-stats>
            <Stat label={t("con.stats.contractors")} value={`${d.counts.contractors}`}
              hint={t("con.stats.split", { firms: d.counts.firms, individuals: d.counts.individuals })} />
            <Stat label={t("con.stats.engagements")} value={`${d.counts.active_engagements}`} hint={t("con.stats.engagementsHint")} />
            <Stat label={t("con.stats.blocked")} value={`${d.counts.blocked}`} hint={t("con.stats.blockedHint")}
              tone={d.counts.blocked ? "red" : "green"} />
            <Stat label={t("con.stats.spend")} value={money(d.cost.this_month.approved_amount)}
              hint={d.cost.this_month.pending_amount ? t("con.stats.pending", { amount: money(d.cost.this_month.pending_amount) }) : t("con.stats.spendHint")} />
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <Card className="p-4" data-con-access>
              <h2 className="font-heading text-base font-semibold">{t("con.access.title")}</h2>
              <p className="mt-1 text-[11px] text-brand-grey">{t("con.access.hint")}</p>
              {d.blocked.length === 0 && d.attention.length === 0 && <p className="mt-3 text-sm text-brand-grey">{t("con.access.allClear")}</p>}
              <ul className="mt-3 space-y-1 text-xs">
                {[...d.blocked, ...d.attention].map((c) => (
                  <li key={c.name}>
                    <Link to={`/contractors/${encodeURIComponent(c.name)}`} className="flex w-full items-center justify-between gap-2 border-t border-brand-grey-light/70 py-1.5 text-left hover:bg-[#F6F8FA]">
                      <span className="min-w-0 flex-1 truncate">
                        <span className="font-medium">{c.contractor_name}</span>
                        <span className="ml-2 text-brand-grey">{t(`con.type.${c.contractor_type}`)}{c.trade ? ` · ${c.trade}` : ""}</span>
                      </span>
                      <span className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold ${GATE_PILL[c.access.gate]}`}>
                        {t(`con.gate.${c.access.gate}`)}
                      </span>
                    </Link>
                    <div className="pb-1 pl-1 text-[11px] text-brand-grey">
                      {c.access.missing.length > 0 && <span className="mr-2 text-status-red">{t("con.access.missing")}: {c.access.missing.join(", ")}</span>}
                      {c.access.expired.length > 0 && <span className="mr-2 text-status-red">{t("con.access.expired")}: {c.access.expired.join(", ")}</span>}
                      {c.access.expiring.length > 0 && <span className="text-status-amber">{t("con.access.expiring")}: {c.access.expiring.join(", ")}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-4" data-con-renewals>
              <h2 className="font-heading text-base font-semibold">{t("con.renewals.title")}</h2>
              <p className="mt-1 text-[11px] text-brand-grey">{t("con.renewals.hint")}</p>
              {d.renewals.length === 0 && <p className="mt-3 text-sm text-brand-grey">{t("con.renewals.none")}</p>}
              <ul className="mt-3 space-y-1 text-xs">
                {d.renewals.map((r) => (
                  <li key={r.name} className="flex items-center justify-between gap-2 border-t border-brand-grey-light/70 py-1.5">
                    <Link to={`/contractors/${encodeURIComponent(r.contractor)}`} className="min-w-0 flex-1 truncate text-left hover:underline">
                      <span className="font-medium">{r.title}</span>
                      <span className="ml-2 text-brand-grey">{r.contractor_name}</span>
                    </Link>
                    <span className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold ${r.ended ? "bg-red-50 text-status-red" : "bg-amber-50 text-status-amber"}`}>
                      {r.ended ? t("con.renewals.ended", { days: Math.abs(r.days_to_end) }) : t("con.renewals.endsIn", { days: r.days_to_end })}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          <Card className="mt-6 p-4" data-con-list>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-heading text-base font-semibold">{t("con.list.title")}</h2>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("common.search")} data-con-search
                className="w-56 rounded border border-brand-grey-light px-3 py-1.5 text-sm focus:border-brand-primary focus:outline-none" />
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                  <tr>
                    <th className="px-3 py-2">{t("con.list.contractor")}</th>
                    <th className="px-3 py-2">{t("con.list.type")}</th>
                    <th className="px-3 py-2">{t("con.list.trade")}</th>
                    <th className="px-3 py-2">{t("con.list.company")}</th>
                    <th className="px-3 py-2">{t("con.list.engagements")}</th>
                    <th className="px-3 py-2 text-right">{t("con.list.spend")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => {
                    const eng = d.engagements.filter((e) => e.contractor === c.name);
                    const spend = d.cost.by_contractor.find((s) => s.key === c.name);
                    return (
                      <tr key={c.name} onClick={() => navigate(`/contractors/${encodeURIComponent(c.name)}`)} data-con-row
                        className="cursor-pointer border-t border-brand-grey-light hover:bg-[#F6F8FA]">
                        <td className="px-3 py-2 font-semibold text-brand-primary">{c.contractor_name}</td>
                        <td className="px-3 py-2">{t(`con.type.${c.contractor_type}`)}</td>
                        <td className="px-3 py-2 text-brand-grey">{c.trade || "–"}</td>
                        <td className="px-3 py-2">{c.company ? <CompanyLogo company={c.company} size={16} showName /> : "–"}</td>
                        <td className="px-3 py-2">
                          {eng.length === 0 ? <span className="text-brand-grey">–</span> : eng.map((e) => (
                            <span key={e.name} className={`mr-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_PILL[e.status] ?? ""}`}>{t(`con.status.${e.status}`, e.status)}</span>
                          ))}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{spend ? money(spend.amount) : "–"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rows.length === 0 && <p className="py-6 text-center text-sm text-brand-grey">{t("common.noRows")}</p>}
            </div>
          </Card>

        </>
      )}
    </AppShell>
  );
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: "red" | "green" }) {
  const colour = tone === "red" ? "text-status-red" : tone === "green" ? "text-status-green" : "text-brand-primary";
  return (
    <Card className="p-4">
      <div className="text-[11px] uppercase tracking-wide text-brand-grey">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-semibold ${colour}`}>{value}</div>
      {hint && <div className="mt-1 text-[11px] text-brand-grey">{hint}</div>}
    </Card>
  );
}
